import {Injectable} from '@angular/core';
import {VexLayoutService} from '@vex/services/vex-layout.service';
import {NavigationItem} from './navigation-item.interface';
import {BehaviorSubject, Observable} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class NavigationLoaderService {
  private readonly _items: BehaviorSubject<NavigationItem[]> =
    new BehaviorSubject<NavigationItem[]>([]);

  get items$(): Observable<NavigationItem[]> {
    return this._items.asObservable();
  }

  constructor(private readonly layoutService: VexLayoutService) {
    this.loadNavigation();
  }

  loadNavigation(): void {
    this._items.next([
      {
        type: 'subheading',
        label: 'Dashboard',
        availableTo: ['ORGANISATION', 'COORDINATOR'],
        children: [
          {
            type: 'link',
            label: 'Home',
            route: '/home',
            icon: 'mat:home',
            routerLinkActiveOptions: {exact: true},
            availableTo: ['ORGANISATION', 'COORDINATOR']
          }
        ]
      },
      {
        type: 'subheading',
        label: 'General',
        availableTo: ['ORGANISATION'],
        children: [
          {
            type: 'dropdown',
            label: 'Coordinators',
            icon: 'mat:admin_panel_settings',
            availableTo: ['ORGANISATION'],
            children: [
              {
                type: 'link',
                label: 'Add',
                route: '/general/coordinator/add',
                availableTo: ['ORGANISATION']
              },
              {
                type: 'link',
                label: 'List',
                route: '/general/coordinator/list',
                availableTo: ['ORGANISATION']
              }
            ]
          },
          {
            type: 'dropdown',
            label: 'Sessions',
            icon: 'mat:access_time',
            availableTo: ['ORGANISATION'],
            children: [
              {
                type: 'link',
                label: 'Add',
                route: '/general/session/add',
                availableTo: ['ORGANISATION']
              },
              {
                type: 'link',
                label: 'List',
                route: '/general/session/list',
                availableTo: ['ORGANISATION']
              }
            ]
          }
        ]
      },

      {
        type: 'subheading',
        label: 'Subjects',
        availableTo: ['ORGANISATION', 'COORDINATOR'],
        children: [
          {
            type: 'link',
            label: 'Add',
            route: '/subject/add',
            icon: 'mat:note_add',
            availableTo: ['ORGANISATION']
          },
          {
            type: 'link',
            label: 'List',
            route: '/subject/list',
            icon: 'mat:book',
            availableTo: ['ORGANISATION', 'COORDINATOR']
          },
        ]
      },
      {
        type: 'subheading',
        label: 'Students',
        availableTo: ['ORGANISATION', 'COORDINATOR'],
        children: [
          {
            type: 'link',
            label: 'Add',
            route: '/student/add',
            icon: 'mat:person_add',
            availableTo: ['ORGANISATION']
          },
          {
            type: 'link',
            label: 'List',
            route: '/student/list',
            icon: 'mat:diversity_3',
            availableTo: ['ORGANISATION', 'COORDINATOR']
          },
          {
            type: 'link',
            label: 'Assign Subjects',
            route: '/student/assign-subjects',
            icon: 'mat:compare_arrows',
            availableTo: ['ORGANISATION']
          },
        ]
      },
      {
        type: 'subheading',
        label: 'Questions',
        availableTo: ['ORGANISATION'],
        children: [
          {
            type: 'link',
            label: 'Upload',
            route: '/questions',
            icon: 'mat:cloud_upload',
            availableTo: ['ORGANISATION']
          },
        ]
      },
      {
        type: 'subheading',
        label: 'Assign',
        availableTo: ['ORGANISATION'],
        children: [
          {
            type: 'link',
            label: 'Invigilator',
            route: '/student-mapping/assign-invigilator',
            icon: 'mat:pageview',
            availableTo: ['ORGANISATION']
          },
          {
            type: 'link',
            label: 'Evaluator',
            route: '/student-mapping/assign-evaluator',
            icon: 'mat:create',
            availableTo: ['ORGANISATION']
          },
        ]
      },
      {
        type: 'subheading',
        label: 'Student Monitoring',
        availableTo: ['ORGANISATION', 'COORDINATOR'],
        children: [
          {
            type: 'dropdown',
            label: 'Exams',
            icon: 'mat:library_books',
            availableTo: ['ORGANISATION', 'COORDINATOR'],
            children: [
              {
                type: 'link',
                label: 'Live',
                route: '/monitoring/live',
                icon: 'mat:library_books',
                availableTo: ['ORGANISATION', 'COORDINATOR']
              },
              {
                type: 'link',
                label: 'Upcoming',
                route: '/monitoring/upcoming',
                icon: 'mat:library_books',
                availableTo: ['ORGANISATION', 'COORDINATOR']
              },
              {
                type: 'link',
                label: 'Monitored',
                route: '/monitoring/monitored',
                icon: 'mat:diversity_3',
                availableTo: ['ORGANISATION', 'COORDINATOR']
              },
            ]
          },
        ]
      },
      {
        type: 'subheading',
        label: 'Evaluation',
        availableTo: ['ORGANISATION', 'COORDINATOR'],
        children: [
          {
            type: 'link',
            label: 'Students',
            route: '/evaluation',
            icon: 'mat:diversity_3',
            availableTo: ['ORGANISATION', 'COORDINATOR']
          },
        ]
      },
      {
        type: 'subheading',
        label: 'Result',
        availableTo: ['ORGANISATION'],
        children: [
          {
            type: 'link',
            label: 'Students',
            route: '/result',
            icon: 'mat:diversity_3',
            availableTo: ['ORGANISATION', 'COORDINATOR']
          },
        ]
      },
      {
        type: 'subheading',
        label: 'Notification',
        availableTo: ['ORGANISATION'],
        children: [
          {
            type: 'link',
            label: 'Send',
            route: '/notification/send',
            icon: 'mat:fiber_new',
            availableTo: ['ORGANISATION']
          },
          {
            type: 'link',
            label: 'List',
            route: '/notification/list',
            icon: 'mat:filter_list',
            availableTo: ['ORGANISATION']
          },
        ]
      },
      {
        type: 'subheading',
        label: 'Support Ticket',
        availableTo: ['ORGANISATION', 'COORDINATOR'],
        children: [
          {
            type: 'link',
            label: 'Add',
            route: '/support-ticket/add',
            icon: 'mat:bookmark',
            availableTo: ['COORDINATOR']
          },
          {
            type: 'link',
            label: 'List',
            route: '/support-ticket',
            icon: 'mat:subject',
            availableTo: ['ORGANISATION', 'COORDINATOR']
          },
        ]
      },
      {
        type: 'subheading',
        label: 'Help & Support',
        availableTo: ['ORGANISATION', 'COORDINATOR'],
        children: [
          {
            type: 'dropdown',
            label: 'Help & Support',
            icon: 'mat:help',
            availableTo: ['ORGANISATION', 'COORDINATOR'],
            children: [
              {
                type: 'link',
                label: 'Introduction',
                route: '/help/introduction',
                icon: 'mat:help',
                availableTo: ['ORGANISATION', 'COORDINATOR']
              },
              {
                type: 'link',
                label: 'Login',
                route: '/help/login',
                icon: 'mat:account_circle',
                availableTo: ['ORGANISATION', 'COORDINATOR']
              },
              {
                type: 'dropdown',
                label: 'Coordinators',
                icon: 'mat:admin_panel_settings',
                availableTo: ['ORGANISATION'],
                children: [
                  {
                    type: 'link',
                    label: 'Add',
                    route: '/help/coordinator/add',
                    availableTo: ['ORGANISATION']
                  },
                  {
                    type: 'link',
                    label: 'Assign Subjects',
                    route: '/help/coordinator/assign-subject',
                    availableTo: ['ORGANISATION']
                  }
                ]
              },
              {
                type: 'link',
                label: 'Sessions',
                route: '/help/session',
                icon: 'mat:access_time',
                availableTo: ['ORGANISATION']
              },
              {
                type: 'dropdown',
                label: 'Subject',
                icon: 'mat:note_add',
                availableTo: ['ORGANISATION'],
                children: [
                  {
                    type: 'link',
                    label: 'Add',
                    route: '/help/subject/add',
                    availableTo: ['ORGANISATION']
                  },
                  {
                    type: 'link',
                    label: 'Schedule Paper',
                    route: '/help/subject/schedule-paper',
                    availableTo: ['ORGANISATION']
                  },
                  {
                    type: 'link',
                    label: 'Upload Questions',
                    route: '/help/subject/upload-questions',
                    availableTo: ['ORGANISATION']
                  }
                ]
              },
              {
                type: 'dropdown',
                label: 'Student',
                icon: 'mat:diversity_3',
                availableTo: ['ORGANISATION'],
                children: [
                  {
                    type: 'link',
                    label: 'Add',
                    route: '/help/student/add',
                    availableTo: ['ORGANISATION']
                  },
                  {
                    type: 'link',
                    label: 'Upload Photograph',
                    route: '/help/student/upload-photograph',
                    availableTo: ['ORGANISATION']
                  },
                  {
                    type: 'link',
                    label: 'Assign Subjects',
                    route: '/help/student/assign-subjects',
                    availableTo: ['ORGANISATION']
                  }
                ]
              },
              {
                type: 'link',
                label: 'Assign Invigilator',
                route: '/help/assign/invigilator',
                availableTo: ['ORGANISATION']
              },
              {
                type: 'link',
                label: 'Assign Evaluator',
                route: '/help/assign/evaluator',
                availableTo: ['ORGANISATION']
              },
              {
                type: 'dropdown',
                label: 'Result',
                icon: 'mat:diversity_3',
                availableTo: ['ORGANISATION'],
                children: [
                  {
                    type: 'link',
                    label: 'Generate',
                    route: '/help/result/generate',
                    availableTo: ['ORGANISATION']
                  },
                  {
                    type: 'link',
                    label: 'Declare',
                    route: '/help/result/declare',
                    availableTo: ['ORGANISATION']
                  }
                ]
              },
              {
                type: 'link',
                label: 'Student Monitoring',
                route: '/help/student-monitoring',
                availableTo: ['COORDINATOR']
              },
              {
                type: 'link',
                label: 'Evaluation',
                route: '/help/evaluation',
                availableTo: ['COORDINATOR']
              },
            ]
          },
        ]
      },
    ]);
  }
}
